import React, { useState, useEffect } from 'react';
import { NavLink, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

// Reuse existing admin settings components
import CompanySettings from '@/modules/Administration/components/settings/CompanySettings';
import UsersSettings from '@/modules/Administration/components/settings/UsersSettings';
import TeamsSettings from '@/modules/Administration/components/settings/TeamsSettings';
import RolesPermissionsSettings from '@/modules/Administration/components/settings/RolesPermissionsSettings';
import BillingSettings from '@/modules/Administration/components/settings/BillingSettings';
import SystemSettings from '@/modules/Administration/components/settings/SystemSettings';
import ModuleManagement from './ModuleManagement';
import OrgIntegrations from './OrgIntegrations';

import { useCompany } from '@/contexts/CompanyContext';

export default function AdminPortal() {
    const location = useLocation();
    const navigate = useNavigate();
    const { currentCompany } = useCompany();

    const userRole = 'superadmin';

    const adminSections = [
        {
            title: 'Account & Team',
            items: [
                { id: 'workspace', icon: 'domain', label: 'Workspace & Branding', path: '/admin/workspace' },
                { id: 'users', icon: 'group', label: 'Users & Permissions', path: '/admin/users' },
                { id: 'billing', icon: 'credit_card', label: 'Billing & Subscription', path: '/admin/billing' }
            ]
        },
        {
            title: 'System & Connectivity',
            items: [
                { id: 'integrations', icon: 'extension', label: 'Integrations & API', path: '/admin/integrations' },
                { id: 'ai-rules', icon: 'smart_toy', label: 'AI Rules & Keys', path: '/admin/ai-rules' },
                { id: 'activity-log', icon: 'history', label: 'Activity Log', path: '/admin/activity-log' },
                { id: 'system', icon: 'settings_suggest', label: 'System Settings', path: '/admin/system-settings' }
            ]
        }
    ];

    return (
        <div className="flex h-full w-full overflow-hidden relative bg-[var(--bg-page)]">
            
            {/* Sidebar */}
            <div className="desktop-only flex flex-col shrink-0 w-[240px] border-r border-[var(--border)] bg-[var(--bg-subtle)]">
                <div className="pt-5 px-4 pb-3">
                    {/* Back Button */}
                    <div
                        onClick={() => navigate('/hub')}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer mb-4 transition-colors duration-150"
                    >
                        <ChevronLeft size={14} /> Back to Hub
                    </div>
                    <div className="flex items-center gap-2">
                        <ObliqueIcon name="admin_panel_settings" size={20} className="text-[var(--text-primary)]" />
                        <h2 className="text-[15px] font-medium text-[var(--text-primary)] tracking-tight">Admin Console</h2>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-3 pb-6">
                    {adminSections.map((section) => (
                        <div key={section.title} className="pb-4">
                            <h4 className="text-[10.5px] font-semibold text-[var(--text-tertiary)] pt-3 px-2.5 pb-1 tracking-wider uppercase">
                                {section.title}
                            </h4>
                            
                            <div className="flex flex-col gap-0.5">
                                {section.items.map(item => {
                                    const isActive = location.pathname.startsWith(item.path);
                                    return (
                                        <NavLink
                                            key={item.id}
                                            to={item.path}
                                            className={`flex items-center gap-2.5 px-2.5 py-1.5 mx-2 rounded-md min-h-[32px] text-[13.5px] transition-all duration-150 ${
                                                isActive
                                                    ? 'font-bold text-[var(--text-primary)] bg-[var(--bg-muted)]'
                                                    : 'font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-muted)]'
                                            }`}
                                        >
                                            <ObliqueIcon 
                                                name={item.icon} 
                                                size={22} 
                                                strokeScale={1.1}
                                                isActive={isActive}
                                                filled={isActive}
                                                className={isActive ? 'text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}
                                            />
                                            {item.label}
                                        </NavLink>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-w-0 bg-[var(--bg-page)]">
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="py-10 px-8 max-w-[1200px] mx-auto">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={location.pathname}
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -5 }}
                                transition={{ duration: 0.15, ease: 'easeOut' }}
                            >
                                <Routes location={location} key={location.pathname}>
                                    <Route path="/" element={<Navigate to="workspace" replace />} />
                                    
                                    <Route path="workspace" element={<CompanySettings currentCompany={currentCompany} userRole={userRole} />} />
                                    <Route path="users" element={<UsersSettings currentCompany={currentCompany} userRole={userRole} />} />
                                    <Route path="teams" element={<TeamsSettings />} />
                                    <Route path="roles" element={<RolesPermissionsSettings />} />
                                    <Route path="permissions" element={<RolesPermissionsSettings />} />
                                    
                                    <Route path="modules" element={<ModuleManagement />} />
                                    <Route path="integrations" element={<OrgIntegrations />} />
                                    <Route path="billing/*" element={<BillingSettings currentCompany={currentCompany} />} />
                                    <Route path="system-settings" element={<SystemSettings />} />
                                    
                                    {/* Placeholders for unbuilt features */}
                                    <Route path="departments" element={<div className="text-[var(--text-tertiary)]">Departments coming soon...</div>} />
                                    <Route path="standards-library" element={<div className="text-[var(--text-tertiary)]">Standards Library coming soon...</div>} />
                                    <Route path="ai-rules" element={<div className="text-[var(--text-tertiary)]">AI Rules coming soon...</div>} />
                                    <Route path="activity-log" element={<div className="text-[var(--text-tertiary)]">Activity Log coming soon...</div>} />
                                </Routes>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </div>
    );
}
